import { useLayout } from "components/layout/LayoutComponentContext";
import { useCallback, useEffect, useState } from "react";
import storage from "services/storage";
import StockPerformance from "./components/StockPerformance";
import PriceChartSection from "./components/PriceChartSection";
import { Grid, Tab, Tabs } from "@mui/material";
import LatestTransactionList from "./components/LatestTransactionList";
import { Title } from "components";
import NumberOfShareholderDistributionSection from "./components/NumberOfShareholderDistributionSection";
import SharesValueDistributionSection from "./components/SharesValueDistributionSection";
import {
  useGetFreeFloatSharesChart,
  useGetStockPerformance,
} from "./services/AnalyticsPageService";
import { useIntersectionObserver } from "usehooks-ts";
import "./AnalyticsPage.css";
import BrokerTable from "./components/BrokerTable";
import { formatDate } from "utils/formatDate";
import InputDatePickerComponent from "components/input-date-picker/InputDatePickerComponent";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import {
  generateUrlWithParams,
  paramParsify,
  paramStringify,
} from "utils/queryParams";

const AnalyticsPage = ({ setLayoutTitle }) => {
  const { companyProfile } = useLayout();
  const companyIdLocalStorage = storage.getCompanyID();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = paramParsify(location?.search);

  const { response: closePriceResp } = useGetStockPerformance({
    id: companyIdLocalStorage,
    type: "CLOSE_PRICE",
  });
  const { response: freeFloatSharesChartResp } = useGetFreeFloatSharesChart({
    id: companyIdLocalStorage,
    defaultDate: formatDate.toAPIDate(closePriceResp?.data?.end_date),
  });

  // handle scroll feature
  const [tabValue, setTabValue] = useState("stockPerformance");
  const { isIntersecting: isInStockPerformanceView, ref: stockPerformanceRef } =
    useIntersectionObserver();
  const { isIntersecting: isInUnderwriterView, ref: underwriterRef } =
    useIntersectionObserver();
  const { isIntersecting: isInFreeFloatView, ref: freeFloatRef } =
    useIntersectionObserver({ threshold: 0.6 });
  const handleOnChangeTab = (_e, val) => {
    document.getElementById(val).scrollIntoView({ behavior: "smooth" });
    setTabValue(val);
  };

  const handleOnChangeDateFreeFloatChart = useCallback(
    (payload) => {
      const date = formatDate.toAPIDate(payload);
      const stringifiedParams = paramStringify({ ...queryParams, date });
      navigate(generateUrlWithParams(location?.pathname, stringifiedParams), {
        replace: true,
      });
    },
    [location?.pathname, navigate, queryParams],
  );

  useEffect(() => {
    if (isInStockPerformanceView) {
      setTabValue("stockPerformance");
    }
    if (isInUnderwriterView) {
      setTabValue("underwriter");
    }
    if (isInFreeFloatView) {
      setTabValue("freeFloat");
    }
  }, [isInStockPerformanceView, isInUnderwriterView, isInFreeFloatView]);

  useEffect(() => {
    setLayoutTitle(
      <div className='flex flex-row gap-2 items-center'>
        <p className='font-bold text-xl'>Advanced Analytics</p>
        <div className='rounded-lg px-2 py-1 bg-[#556FB5]'>
          <p className='text-sm text-white font-bold'>Pro</p>
        </div>
      </div>,
    );
    return () => setLayoutTitle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className='analytics-page grid gap-4'>
      <div className='flex flex-row gap-2 items-center'>
        <p className='font-bold text-lg'>{storage.getItem("effectCode")}</p>
        <p className='text-[#5F737F] mt-[1.25px]'>{companyProfile?.name}</p>
      </div>
      <Tabs onChange={handleOnChangeTab} value={tabValue}>
        <Tab label='Stock Performance' value='stockPerformance' />
        <Tab
          label='Underwriter/Broker Shares Transaction Summary'
          value='underwriter'
        />
        <Tab label='Free Float Shares' value='freeFloat' />
      </Tabs>
      <div className='max-h-[60vh] overflow-y-scroll'>
        <div
          ref={stockPerformanceRef}
          id='stockPerformance'
          className='flex flex-col gap-4 mt-5 mb-5'
        >
          <StockPerformance closePriceResp={closePriceResp} />
          <PriceChartSection />
        </div>

        <div ref={underwriterRef} id='underwriter' className='mb-5'>
          <p className='font-semibold mb-5'>
            Underwriter/Broker Shares Transaction Summary
          </p>
          <BrokerTable
            defaultStartDate={formatDate.toAPIDate(
              closePriceResp?.data?.start_date,
            )}
            defaultEndDate={formatDate.toAPIDate(
              closePriceResp?.data?.end_date,
            )}
          />
        </div>

        <div ref={freeFloatRef} id='freeFloat'>
          <div className='flex justify-between items-center mb-5'>
            <Title
              titleClass='text-base font-semibold'
              tooltipText='Shareholder with ownership less than 5%'
              title='Free Float Shares'
            />
            <InputDatePickerComponent
              value={formatDate.toObjectDayJs(
                queryParams?.date || closePriceResp?.data?.end_date,
              )}
              onChange={handleOnChangeDateFreeFloatChart}
              maxDate={dayjs()}
              disableWeekends={true}
              limitToFiveYears={true}
            />
          </div>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <NumberOfShareholderDistributionSection
                data={freeFloatSharesChartResp?.data?.shareholder_distribution}
              />
            </Grid>
            <Grid item xs={6}>
              <SharesValueDistributionSection
                data={freeFloatSharesChartResp?.data?.shares_value_distribution}
              />
            </Grid>
            <Grid item xs={12}>
              <LatestTransactionList
                defaultPeriodStart={closePriceResp?.data?.start_date}
                defaultPeriodEnd={closePriceResp?.data?.end_date}
                closePriceResp={closePriceResp}
              />
            </Grid>
          </Grid>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
